import { Account as IAccount, Bundle, UpdateAccountRequest } from '@lune-climate/lune'
import { useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { ButtonNew, LoadingWrapper, MainLayoutContainerNew } from '@lune-fe/lune-ui-lib'
import { memo, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import AccountFormWrapper, {
    AccountFormKeys,
    AccountFormType,
    BundleSettings,
    IAccountFormData,
} from 'components/AccountForm/AccountFormWrapper'
import NotAdminTooltip from 'components/NotAdminTooltip'
import { removeAccountLogo, uploadAccountLogo } from 'endpoints/api'
import useAccounts from 'hooks/useAccounts'
import useBundlesPortfolios from 'hooks/useBundlesPortfolios'
import useIndividualAccountBundles from 'hooks/useIndividualAccountBundles'
import useIsTestMode from 'hooks/useIsTestMode'
import { useLuneClient } from 'hooks/useLuneClient'
import useTitle from 'hooks/useTitle'
import { useIsAuthorised } from 'hooks/useUserState'
import { SnackbarMessages } from 'SnackbarMessages'
import { CUSTOM_PORTFOLIO_ID } from 'views/Settings/BundlePicker/BundlePicker'

const Account = () => {
    const isTestMode = useIsTestMode()
    const isAuthorised = useIsAuthorised()
    const { accounts, refetchAccounts } = useAccounts()
    const [account, setAccount] = useState<IAccount>()
    const [bundleSelection, setBundleSelection] = useState<Record<string, number | string>>()
    const [bundles, setBundles] = useState<Bundle[]>()
    const [formValue, setFormValue] = useState<IAccountFormData>()
    const [bundleSettings, setBundleSettings] = useState<BundleSettings>()
    const [bundlePickerError, setBundlePickerError] = useState<boolean>(false)
    const { accountId } = useParams<{ accountId: string }>()
    const luneClient = useLuneClient()
    const { bundlesPortfolios, loading: loadingBundles } = useBundlesPortfolios()
    const [logoUpdating, setLogoUpdating] = useState<boolean>(false)

    useTitle(account?.name)

    const { enqueueSnackbar: snackbar } = useErrorSnackbar()
    const { loadBundles, loadAllBundles, updateCustomBundleSelection } =
        useIndividualAccountBundles()

    const onUpdateSuccess = () => {
        refetchAccounts()
        snackbar(SnackbarMessages.SETTINGS_SAVED)
    }

    const handleLogoUpdate = useCallback(
        async (file: File | null) => {
            if (!account) {
                return
            }
            setLogoUpdating(true)
            try {
                if (file) {
                    // Upload new logo
                    const formData = new FormData()
                    formData.append('logo', file)
                    const result = await uploadAccountLogo(account.id, formData)
                    if (result.isOk()) {
                        onUpdateSuccess()
                    } else {
                        snackbar(SnackbarMessages.GENERIC_ERROR, {
                            variant: 'error',
                            error: result.error,
                        })
                    }
                } else {
                    const result = await removeAccountLogo(account.id)
                    if (result.isOk()) {
                        onUpdateSuccess()
                    } else {
                        snackbar(SnackbarMessages.GENERIC_ERROR, {
                            variant: 'error',
                            error: result.error,
                        })
                    }
                }
            } catch (error) {
                console.error('Failed to update logo:', error)
                snackbar(SnackbarMessages.GENERIC_ERROR, {
                    variant: 'error',
                    error,
                })
            } finally {
                setLogoUpdating(false)
            }
        },
        [account, snackbar, onUpdateSuccess],
    )

    const onSubmit = async (data: IAccountFormData) => {
        refetchAccounts()

        if (!account) {
            return
        }

        await updateCustomBundleSelection(account.id, data.bundleSettings.customBundleSelection)
        const selectedPortfolioId = data.bundleSettings.selectedBundlePortfolioId
        return luneClient
            .updateAccount(account.id, {
                name: data.name,
                beneficiary: data.beneficiary,
                handle: data.handle,
                externalPagesVisibility: data.externalPagesVisibility,
                bundlePortfolioId:
                    selectedPortfolioId === CUSTOM_PORTFOLIO_ID ? undefined : selectedPortfolioId,
            } as UpdateAccountRequest & { bundlePortfolioId?: string })
            .then((r) => r.unwrap())
    }

    useEffect(() => {
        const account = accounts.find((acc) => acc.id === accountId)

        if (!account) {
            return
        }

        setAccount(account)

        loadBundles(account.id).then((bundleSelection) => {
            setBundleSelection(bundleSelection)
        })
        loadAllBundles(account.id).then((bundles) => {
            setBundles(bundles)
        })
    }, [accountId, accounts, loadAllBundles, loadBundles])

    return (
        <LoadingWrapper
            loading={!bundleSelection || !account || loadingBundles}
            sx={{
                minHeight: '100vh',
            }}
        >
            {account && !loadingBundles && (
                <MainLayoutContainerNew
                    isTestMode={!!isTestMode}
                    title={account.name}
                    primaryButton={
                        <ButtonNew
                            data-testid="account-submit-btn"
                            disabled={
                                !formValue?.name?.trim() ||
                                !formValue.currency ||
                                bundlePickerError ||
                                !isAuthorised
                            }
                            onClick={async () => {
                                if (formValue && bundleSettings) {
                                    const result = await onSubmit({
                                        name: formValue.name,
                                        currency: formValue.currency,
                                        beneficiary: formValue.beneficiary,
                                        handle: formValue.handle,
                                        externalPagesVisibility: formValue.externalPagesVisibility,
                                        bundleSettings,
                                    })
                                    if (result) {
                                        onUpdateSuccess()
                                    }
                                }
                            }}
                        >
                            Save changes
                        </ButtonNew>
                    }
                >
                    {/* Key prop forces account form to re-mount when changing between accounts
                        This helps keep the BundlePicker data consistent */}
                    <div key={`${JSON.stringify(bundleSelection)}_${accountId}`}>
                        <AccountFormWrapper
                            formType={AccountFormType.ACCOUNT}
                            bundles={bundles}
                            readOnly={!isAuthorised}
                            existingAccount
                            onSubmit={onSubmit}
                            onSuccess={onUpdateSuccess}
                            initialValues={{
                                name: account.name,
                                currency: account.currency,
                                beneficiary: account.beneficiary ?? undefined,
                                handle: account.handle ?? undefined,
                                externalPagesVisibility: account.externalPagesVisibility,
                                bundleSettings: {
                                    customBundleSelection: bundleSelection!,
                                    selectedBundlePortfolioId:
                                        account.bundlePortfolioId ?? CUSTOM_PORTFOLIO_ID,
                                },
                            }}
                            disabledFields={isTestMode ? [AccountFormKeys.NAME] : undefined}
                            forbiddenTooltip={NotAdminTooltip}
                            onFormValueChange={(formVal, bundleSettings, bundleError) => {
                                setFormValue(formVal)
                                setBundleSettings(bundleSettings)
                                setBundlePickerError(bundleError)
                            }}
                            bundlesPortfolios={bundlesPortfolios}
                            account={account}
                            onLogoUpdate={handleLogoUpdate}
                            logoUpdating={logoUpdating}
                        />
                    </div>
                </MainLayoutContainerNew>
            )}
        </LoadingWrapper>
    )
}

export default memo(Account)
